/**
 * The plan (PLAN §7, v0.2 "The Plan"). The solver has been a tested library
 * with nothing rendering it; this is the view it was written for.
 *
 * One leg with Encore mode off, two with it on — the same split the countdown
 * bar and the filters already use, read from the same settings store, so the
 * plan and the countdown can never quote different deadlines.
 */

import { formatMinutes } from "@/components/entry/RuntimeChip.tsx";
import { formatDays, formatDeadline } from "@/lib/countdown/format.ts";
import { usePlan } from "@/lib/plan/context.tsx";
import type { LegPlan, PlanWarning } from "@/lib/solver/index.ts";
import { CutList } from "./CutList.tsx";
import styles from "./plan.module.css";
import { WeekPlanTimeline } from "./WeekPlanTimeline.tsx";

function warningText(w: PlanWarning): string {
  switch (w.kind) {
    case "approximate-fill":
      return "This fill is close to optimal rather than provably optimal: some stops share prerequisites, which makes the exact solve too expensive.";
    case "infeasible-leg":
      return `${formatMinutes(w.deficitMinutes)} of essential viewing does not fit this leg.`;
    // Aggregated in `Leg` rather than printed per occurrence — the solver
    // emits one of these per offending entry.
    case "unmet-prerequisites":
      return "";
    case "no-days-left":
      return "This leg has no days left in its window.";
    case "schedule-overruns-deadline":
      return `The minutes fit, but the weekly schedule runs ${formatDays(w.overrunDays)} past the deadline. One long stop can take a whole week and waste the rest of it.`;
  }
}

function Leg({ leg, warnings }: { leg: LegPlan; warnings: PlanWarning[] }) {
  const mine = warnings.filter((w) => "legId" in w && w.legId === leg.id);
  // The solver emits one `unmet-prerequisites` per offending entry, so a leg
  // with six of them would otherwise print the same sentence six times under
  // one React key. Collapse to a single line that says how many.
  const unmet = mine.filter((w) => w.kind === "unmet-prerequisites");
  const rest = mine.filter((w) => w.kind !== "unmet-prerequisites");

  return (
    <section className={styles.leg} aria-labelledby={`plan-leg-${leg.id}`}>
      <header className={styles.legHead}>
        <h3 className={styles.legTitle} id={`plan-leg-${leg.id}`}>
          {leg.eventTitle}
        </h3>
        <p className={styles.legDeadline}>
          {formatDeadline(leg.deadline)} · {formatDays(leg.daysLeft)} left
        </p>
      </header>

      <dl className={styles.stats}>
        <div>
          <dt>Planned</dt>
          <dd>
            {leg.selectedCount} of {leg.candidateCount} stops
          </dd>
        </div>
        <div>
          <dt>Runtime</dt>
          <dd>{formatMinutes(leg.selectedMinutes)}</dd>
        </div>
        <div>
          <dt>Capacity</dt>
          <dd>{formatMinutes(leg.capacityMinutes)}</dd>
        </div>
        <div>
          <dt>{leg.feasible ? "Left over" : "Short by"}</dt>
          <dd className={leg.feasible ? "" : styles.deficit}>
            {formatMinutes(leg.feasible ? leg.unusedMinutes : leg.deficitMinutes)}
          </dd>
        </div>
      </dl>

      {rest.map((w) => (
        <p key={w.kind} className={styles.warning}>
          {warningText(w)}
        </p>
      ))}

      {unmet.length > 0 && (
        <p className={styles.warning}>
          {unmet.length} planned {unmet.length === 1 ? "stop has" : "stops have"} prerequisites that
          aren't in the plan. They were filtered out, so the order below assumes setup you haven't
          allowed.
        </p>
      )}

      <WeekPlanTimeline leg={leg} />

      <ol className={styles.order}>
        {leg.selected.map((entry, i) => (
          <li key={entry.entryId} className={styles.orderItem}>
            <span className={styles.orderIndex}>{i + 1}</span>
            <span className={styles.orderTitle}>{entry.title}</span>
            {entry.mandatory && <span className={styles.tagMandatory}>essential</span>}
            {entry.prerequisite && <span className={styles.tagPrereq}>sets up a pick</span>}
            {/* Live since the curation pass wrote runtime.alternatives: the
             * seasons carry essential-episode and summary routes, so this
             * renders whenever the solver took one. */}
            {entry.compressed && entry.route && (
              <span
                className={styles.tagCompressed}
                title={`${entry.route.label}: ${formatMinutes(entry.route.minutes)}`}
              >
                compressed
              </span>
            )}
            <span className={styles.orderCost}>{formatMinutes(entry.costMinutes)}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

export function PlanView() {
  const plan = usePlan();

  if (plan.totals.candidateCount === 0) {
    return (
      <div className={styles.empty}>
        <h2>Nothing to plan.</h2>
        <p>Every stop is filtered out or already watched, so there's no route left to solve.</p>
      </div>
    );
  }

  return (
    <div className={styles.plan}>
      <header className={styles.planHead}>
        <h2 className={styles.planTitle}>Your plan</h2>
        <p className={styles.planSub}>
          {plan.totals.selectedCount} stops · {formatMinutes(plan.totals.selectedMinutes)} at{" "}
          {plan.hoursPerWeek} h/week
          {plan.totals.watchedCount > 0 && ` · ${plan.totals.watchedCount} already watched`}
        </p>
      </header>

      {plan.legs.map((leg) => (
        <Leg key={leg.id} leg={leg} warnings={plan.warnings} />
      ))}

      <CutList cuts={plan.cuts} />
    </div>
  );
}
